import { useState, useEffect, ReactElement } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { api } from "../services/api";
import { isDemoMode } from "../utils/demoMode";
import { DemoModeMessage } from "../utils/demoModeMessage";
import { WorkspaceInvitation } from "../types";
import { workspaceNameToSlug, defaultTransactionIdPrefixFromName } from "../utils/slug";
import { ensureUserDocumentExists, validateUserDocumentExists } from "../utils/memberUtils";
import logo from "../assets/BudgetTrace Logomark_V2.svg";

import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";

// Import ISO-4217 currencies
import { ISO4217_CURRENCIES } from "../utils/iso4217Currencies";

const COMMON_CURRENCIES: readonly { code: string; name: string; symbol: string }[] = ISO4217_CURRENCIES.map((c) => ({
  code: c.code,
  name: c.name,
  symbol: c.symbol,
}));

interface CreateWorkspaceProps {
  onCreated: (workspaceId: string) => void;
}

function CreateWorkspace({ onCreated }: CreateWorkspaceProps): ReactElement {
  const navigate = useNavigate();
  const [name, setName] = useState<string>("");
  const [defaultCurrency, setDefaultCurrency] = useState<typeof COMMON_CURRENCIES[number] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pendingInvitations, setPendingInvitations] = useState<Array<WorkspaceInvitation & { workspaceName?: string }>>([]);
  const [loadingInvitations, setLoadingInvitations] = useState<boolean>(true);
  const [acceptingInvitation, setAcceptingInvitation] = useState<string | null>(null);
  
  // Snackbar for demo mode alerts
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  // Fetch pending invitations for the user
  useEffect(() => {
    const fetchPendingInvitations = async (): Promise<void> => {
      const user = auth.currentUser;
      if (!user || !user.email) {
        setLoadingInvitations(false);
        return;
      }

      try {
        const invitations = await api.getUserInvitations(user.email);
        // Filter to only pending invitations and map to include workspaceName
        const pending = invitations
          .filter(inv => inv.status === "pending")
          .map(inv => ({
            ...inv,
            workspaceName: inv.workspaceName,
          }));
        setPendingInvitations(pending as Array<WorkspaceInvitation & { workspaceName?: string }>);
      } catch (err) {
        console.error("Error fetching pending invitations:", err);
        // Don't show error - just don't show invitations
      } finally {
        setLoadingInvitations(false);
      }
    };

    fetchPendingInvitations();
  }, []);

  const handleCreate = async (): Promise<void> => {
    if (!name.trim() || !defaultCurrency) return;

    // In demo mode, workspace cannot be created
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Workspaces cannot be created. Please log in to create a workspace." />
      );
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) return;

      // Use backend API to create workspace (this will automatically copy account groups from Reference Workspace)
      const workspace = await api.createWorkspace(name.trim(), defaultCurrency.code);

      // Set default transaction ID prefix in settings so it appears in Settings > Workspace (works in deployed and local)
      const defaultPrefix = defaultTransactionIdPrefixFromName(name.trim());
      if (defaultPrefix) {
        try {
          const settingsRef = doc(db, "workspaces", workspace.id, "settings", "general");
          await setDoc(settingsRef, { transactionIdPrefix: defaultPrefix }, { merge: true });
        } catch (prefixErr) {
          console.warn("Could not set initial transaction ID prefix:", prefixErr);
        }
      }

      // Notify App with the created workspace ID
      onCreated(workspace.id);
    } catch (err) {
      console.error("Workspace creation failed:", err);
      // You might want to show an error message to the user here
      alert(err instanceof Error ? err.message : "Failed to create workspace. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (): Promise<void> => {
    await signOut(auth);
  };

  const handleAcceptInvitation = async (invitationId: string, workspaceId: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;

    setAcceptingInvitation(invitationId);
    try {
      const userId = user.uid;
      const userEmail = user.email?.toLowerCase().trim();

      // Get invitation details to verify email
      const invitation = await api.getInvitation(invitationId);
      
      // Verify email matches
      if (userEmail !== invitation.email.toLowerCase().trim()) {
        alert(`This invitation was sent to ${invitation.email}. Please sign in with that email address.`);
        setAcceptingInvitation(null);
        return;
      }

      // Ensure user document exists in users collection (create if doesn't exist)
      // This prevents orphaned members from being created
      await ensureUserDocumentExists(user);
      
      // Validate user document exists before creating member
      const userDocumentValid = await validateUserDocumentExists(userId);
      if (!userDocumentValid) {
        alert("Failed to create user account. Please try again.");
        setAcceptingInvitation(null);
        return;
      }

      // Check if user is already a member
      const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        // Already a member, just update invitation and redirect
        await updateDoc(doc(db, "workspaces", workspaceId, "invitations", invitationId), {
          status: "accepted",
        });
        // Update active workspace
        await updateDoc(doc(db, "users", userId), {
          activeWorkspaceId: workspaceId,
        });
        // Navigate to workspace
        const slug = workspaceNameToSlug(invitation.workspaceName);
        navigate(`/${slug}/activity`);
        return;
      }

      // Get role from invitation
      const memberRole = (invitation.role && ["admin", "user", "viewer"].includes(invitation.role)) 
        ? invitation.role 
        : "user";
      
      // Add user to workspace members
      await setDoc(memberRef, {
        uid: userId,
        displayName: user.displayName || null,
        email: user.email || null,
        role: memberRole,
        joinedAt: serverTimestamp(),
      });

      // Update invitation status
      await updateDoc(doc(db, "workspaces", workspaceId, "invitations", invitationId), {
        status: "accepted",
      });

      // Update user's active workspace
      await updateDoc(doc(db, "users", userId), {
        activeWorkspaceId: workspaceId,
      });

      // Navigate to workspace
      const slug = workspaceNameToSlug(invitation.workspaceName);
      navigate(`/${slug}/activity`);
    } catch (err) {
      console.error("Error accepting invitation:", err);
      alert(err instanceof Error ? err.message : "Failed to accept invitation. Please try again.");
      setAcceptingInvitation(null);
    }
  };

  const formatDate = (timestamp: Timestamp | Date | undefined): string => {
    if (!timestamp) return "Unknown";
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getRoleDisplayName = (role?: string): string => {
    const roleMap: Record<string, string> = {
      admin: "Admin",
      user: "User",
      viewer: "Viewer",
    };
    return role ? roleMap[role] || role : "User";
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
    >
      <Paper
        elevation={0}
        sx={{ width: 400, maxWidth: "90vw", p: 4, borderRadius: 3 }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Budget Trace"
            sx={{
              width: 96,
              height: "auto",
              mb: 1,
            }}
          />
          
          {/* Pending Invitations */}
          {!loadingInvitations && pendingInvitations.length > 0 && (
            <>
              <Typography variant="h6" align="center" sx={{ width: "100%" }}>
                You've been invited!
              </Typography>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardContent>
                  <Stack spacing={2}>
                    {pendingInvitations.map((invitation) => (
                      <Box key={invitation.id}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body1" fontWeight="medium" sx={{ flexGrow: 1 }}>
                            {invitation.workspaceName || "Workspace"}
                          </Typography>
                          {invitation.role && (
                            <Chip label={getRoleDisplayName(invitation.role)} size="small" />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          You've been invited to join this workspace as {getRoleDisplayName(invitation.role)}.
                        </Typography>
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          onClick={() => {
                            if (invitation.workspaceId) {
                              handleAcceptInvitation(invitation.id, invitation.workspaceId);
                            }
                          }}
                          disabled={acceptingInvitation === invitation.id || loading}
                        >
                          {acceptingInvitation === invitation.id ? "Accepting..." : "Accept Invitation"}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Divider sx={{ width: "100%" }} />
              <Typography variant="body2" color="text.secondary" align="center">
                Or create your own workspace below
              </Typography>
            </>
          )}

          {!loadingInvitations && pendingInvitations.length === 0 && (
            <>
              <Typography variant="h6" align="center">
                Create your first workspace
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                Workspaces help you organize expenses
              </Typography>
            </>
          )}

          <TextField
            fullWidth
            placeholder="Workspace name (e.g. Personal Finance)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />

          <Autocomplete
            fullWidth
            options={COMMON_CURRENCIES}
            value={defaultCurrency}
            onChange={(_, newValue) => setDefaultCurrency(newValue)}
            getOptionLabel={(option) => `${option.code} - ${option.name} (${option.symbol})`}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  option.code.toLowerCase().includes(searchValue) ||
                  option.name.toLowerCase().includes(searchValue) ||
                  option.symbol.toLowerCase().includes(searchValue)
              );
            }}
            ListboxProps={{
              sx: {
                maxHeight: 200,
                overflow: "auto",
              },
              onWheelCapture: (e: React.WheelEvent<HTMLUListElement>) => {
                const el = e.currentTarget;
                el.scrollTop += e.deltaY;
                e.preventDefault();
                e.stopPropagation();
              },
            }}
            componentsProps={{
              popper: {
                placement: "bottom-start" as const,
                modifiers: [
                  {
                    name: "flip",
                    enabled: false,
                  },
                ],
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Default Currency"
                placeholder="Select default currency"
                required
              />
            )}
            disabled={loading}
          />

          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              sx={{ flex: 1, py: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={loading || !name.trim() || !defaultCurrency}
              sx={{ flex: 1, py: 1.5 }}
            >
              {loading ? "Creating..." : "Create"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {snackbarElement || snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CreateWorkspace;
